import { Suspense, ReactNode, useLayoutEffect } from 'react';
import { CssBaseline, Container, Typography, Button, Box } from '@mui/material';
import { Loading, CheckForApplicationUpdate } from 'react-admin';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import { RealtimeAnimationStyles } from '../../styles/realtimeAnimations';
import { isFullWidthRoute } from './fullWidthRoutes';
import { FooterLinks } from '../../components/FooterLinks';
import { LegalGate } from '../../legal/LegalGate';
import { useNavMode } from './useNavMode';
import { useNavSections } from './useNavSections';
import { useEditingOutsideDock } from './useEditingOutsideDock';
import {
    CHROME_BOTTOM_VAR,
    CHROME_TOP_VAR,
    HEADER_HEIGHT_PX,
    GUTTER_PADDING,
    SAFE_AREA_LEFT,
    SAFE_AREA_RIGHT,
    chromeVars,
    hasOwnSearch,
    padLeft,
    padRight,
} from './chrome';
import { MobileGlobalStyles } from './MobileGlobalStyles';
import { MobileShell } from './mobile/MobileShell';
import { PushAndPresence } from './PushAndPresence';

export const HEADER_OFFSET = HEADER_HEIGHT_PX;

const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => (
    <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
            Something went wrong
        </Typography>
        <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, whiteSpace: 'pre-wrap' }}
        >
            {error?.message || String(error)}
        </Typography>
        <Button variant="contained" onClick={resetErrorBoundary}>
            Try again
        </Button>
    </Box>
);

export const Layout = ({ children }: { children: ReactNode }) => {
    const { pathname } = useLocation();
    const fullWidth = isFullWidthRoute(pathname);
    const mode = useNavMode();
    const sections = useNavSections();
    // A tab bar with a single tab is noise: one-section users get More in the top bar.
    const showTabBar = mode === 'bottom' && sections.length > 1;
    const showSearchRow = mode === 'bottom' && !hasOwnSearch(pathname);
    // Typing in a field of the page (not the dock's search): the dock steps
    // aside so the field is not squeezed between it and the keyboard.
    const editing = useEditingOutsideDock(mode === 'bottom');
    const chrome = chromeVars({
        mode,
        tabBar: showTabBar,
        searchRow: showSearchRow,
        editing,
    });

    // Published on <html> so PageFrame and full-height pages (CONTENT_HEIGHT)
    // fit between the bars. Layout effect: set before the first paint.
    useLayoutEffect(() => {
        const root = document.documentElement.style;
        root.setProperty(CHROME_TOP_VAR, chrome.top);
        root.setProperty(CHROME_BOTTOM_VAR, chrome.bottom);
    }, [chrome.top, chrome.bottom]);

    return (
        <>
            <CssBaseline />
            <MobileGlobalStyles />
            {/* Everything below is behind the legal gate: a user who has not
                accepted the current legal documents gets the dialog instead,
                so no page loads data and the presence and push-token hooks
                do not run. */}
            <LegalGate>
                <RealtimeAnimationStyles />
                {/* Once per session: the shell below remounts when the width
                    crosses 900 px, this does not. */}
                <PushAndPresence />
                {mode === 'bottom' ? (
                    <MobileShell
                        sections={sections}
                        showTabBar={showTabBar}
                        showSearchRow={showSearchRow}
                        dockHidden={editing}
                    />
                ) : (
                    <Header
                        variant={mode === 'condensed' ? 'condensed' : 'full'}
                    />
                )}
                <Container
                    maxWidth={false}
                    sx={{
                        mt: `var(${CHROME_TOP_VAR}, ${HEADER_HEIGHT_PX}px)`,
                        pb: `var(${CHROME_BOTTOM_VAR}, 0px)`,
                        maxWidth: fullWidth ? '100%' : { xl: 1280 },
                        // The container's own gutters plus the notch inset of
                        // a landscape phone. Edge-to-edge pages are 0 on
                        // phones only: from sm on the container's 24 px gutter
                        // has always won over their px: 0.
                        ...(fullWidth
                            ? {
                                  pl: {
                                      xs: SAFE_AREA_LEFT,
                                      sm: padLeft(24),
                                  },
                                  pr: {
                                      xs: SAFE_AREA_RIGHT,
                                      sm: padRight(24),
                                  },
                              }
                            : GUTTER_PADDING),
                    }}
                >
                    <main id="main-content">
                        <ErrorBoundary FallbackComponent={ErrorFallback}>
                            <Suspense fallback={<Loading />}>
                                {children}
                            </Suspense>
                        </ErrorBoundary>
                    </main>
                </Container>
                <CheckForApplicationUpdate interval={30 * 1000} />
                {/* On phones the links live in the More sheet. */}
                {pathname === '/markets' && mode !== 'bottom' && (
                    <FooterLinks />
                )}
            </LegalGate>
        </>
    );
};
